import React, { useMemo, useState } from 'react';
import { FiCheckCircle, FiRotateCcw } from 'react-icons/fi';
import { cn } from '@/lib/utils';

function shuffle(array) {
  return [...array].sort(() => Math.random() - 0.5);
}

// Ordena por toque (não drag): toca nos itens na ordem certa, cada toque ganha o próximo
// número; tocar de novo desfaz. "Conferir" mostra acerto/erro por posição.
export function SequenceOrder({ items }) {
  const shuffled = useMemo(() => shuffle(items), [items]);
  const [order, setOrder] = useState([]);
  const [checked, setChecked] = useState(false);

  const positionOf = item => order.indexOf(item) + 1;

  const handleTap = item => {
    if (checked) return;
    setOrder(prev => (prev.includes(item) ? prev.filter(i => i !== item) : [...prev, item]));
  };

  const reset = () => {
    setOrder([]);
    setChecked(false);
  };

  const allPlaced = order.length === items.length;
  const correctCount = checked ? order.filter((item, i) => item === items[i]).length : 0;

  return (
    <div className="flex flex-col gap-5 rounded-2xl border border-solid border-neutral-100 bg-white p-6 shadow-card">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-primary-dark">Toque nos itens na ordem certa</p>
        {order.length > 0 && (
          <button
            type="button"
            onClick={reset}
            className="flex items-center gap-1 text-xs font-bold text-neutral-500"
          >
            <FiRotateCcw className="h-3.5 w-3.5" />
            Recomeçar
          </button>
        )}
      </div>

      <div className="flex flex-col gap-2">
        {shuffled.map(item => {
          const pos = positionOf(item);
          const isCorrectPos = checked && items[pos - 1] === item;
          const isWrongPos = checked && pos > 0 && items[pos - 1] !== item;
          return (
            <button
              key={item}
              type="button"
              onClick={() => handleTap(item)}
              disabled={checked}
              className={cn(
                'flex items-center gap-3 rounded-xl border-2 border-solid p-3.5 text-left text-sm font-medium transition-colors',
                pos > 0 ? 'border-primary-light bg-primary-lightest text-primary-dark' : 'border-neutral-lightest bg-white text-neutral-darker',
                isCorrectPos && 'border-success bg-success-lightest text-success',
                isWrongPos && 'border-rose-400 bg-rose-50 text-rose-500'
              )}
            >
              <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 border-solid text-xs font-bold', pos > 0 ? 'border-primary bg-white text-primary' : 'border-neutral-lightest bg-bright text-neutral-400')}>
                {pos > 0 ? pos : ''}
              </span>
              {item}
            </button>
          );
        })}
      </div>

      {!checked ? (
        <button
          type="button"
          disabled={!allPlaced}
          onClick={() => setChecked(true)}
          className="inline-flex items-center justify-center rounded-full bg-primary px-6 py-3 font-bold text-white transition active:scale-[0.98] disabled:cursor-default disabled:opacity-30"
        >
          Conferir ordem
        </button>
      ) : (
        <div className={cn('flex items-center gap-2 rounded-xl px-4 py-3 font-bold', correctCount === items.length ? 'bg-success-lightest text-success' : 'bg-rose-50 text-rose-500')}>
          <FiCheckCircle className="h-5 w-5 shrink-0" />
          {correctCount === items.length ? 'Ordem perfeita!' : `${correctCount}/${items.length} na posição certa — toque em "Recomeçar" pra tentar de novo.`}
        </div>
      )}
    </div>
  );
}

export default SequenceOrder;
